import { useEffect } from "react";
import useEscapeKey from "@hooks/useEscapeKey";
import { useTheme } from "@hooks/useTheme";

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = "md",
  className = "",
  showCloseButton = true,
  "data-testid": dataTestId = "modal",
}) => {
  const { colors } = useTheme();
  useEscapeKey(onClose, isOpen);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: "max-w-md",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
    full: "max-w-full mx-4",
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm ${colors.bgBlurModal}`}
      data-testid={`${dataTestId}-overlay`}
    >
      <div
        className={`relative w-full ${sizeClasses[size]} max-h-[90vh] transform overflow-hidden rounded-lg shadow-xl transition-all duration-200 ${colors.bgModal} ${className} `}
        onClick={(e) => e.stopPropagation()}
        data-testid={dataTestId}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div
            className={`flex items-center justify-between border-b p-6 ${colors.borderPrimary}`}
            data-testid={`${dataTestId}-header`}
          >
            {title && (
              <h3
                className={`text-lg font-semibold ${colors.textPrimary}`}
                data-testid={`${dataTestId}-title`}
              >
                {title}
              </h3>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className={`${colors.iconMuted} transition-colors duration-200 ${colors.hoverTextSecondary}`}
                data-testid={`${dataTestId}-close-button`}
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="max-h-[calc(90vh-8rem)] overflow-y-auto" data-testid={`${dataTestId}-content`}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
